/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import Button, { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import { SelectPresentationProps } from "@eisinc/MobiusOverrides/Select";
import { TextFieldProps } from "@eisinc/MobiusOverrides/TextField";
import Tooltip from "@eisinc/MobiusOverrides/Tooltip/Tooltip";
import ProductAddToCartButton from "@eisinc/Overrides/Components/ProductAddToCartButton";
import ProductAddToListLink, { ProductAddToListLinkStyles } from "@eisinc/Overrides/Components/ProductAddToListLink";
import ProductPrice, { ProductPriceStyles } from "@eisinc/Overrides/Components/ProductPrice";
import ProductQuantityOrdered from "@eisinc/Overrides/Components/ProductQuantityOrdered";
import Info from "@eisinc/XDL/Icons/Info";
import Measure from "@eisinc/XDL/Icons/Measure";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getIsProductCompareFull,
    getProductCompareChecked,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import addProductToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/AddProductToCompare";
import removeProductFromCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductQuantityBreakPricingStyles } from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import Clickable from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon/Icon";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showPrice: boolean;
    showAddToList: boolean;
    showCompare: boolean;
}

const mapStateToProps = (state: ApplicationState, props: HasProductContext) => {
    return {
        productSettings: getSettingsCollection(state).productSettings,
        customSettings: getSettingsCollection(state).customSettings,
        cutWireSettings: getSettingsCollection(state).cutWireSettings,
        view: state.pages.productList.view || getSettingsCollection(state).productSettings.defaultViewType,
        productCompareChecked: getProductCompareChecked(state, props.productContext),
        isProductCompareFull: getIsProductCompareFull(state),
    };
};

const mapDispatchToProps = {
    addProductToCompare,
    removeProductFromCompare,
};

type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

export interface ProductListActionsStyles {
    wrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;
    compareCheckbox?: CheckboxPresentationProps;
    compareAddToListWrapper?: InjectableCss;
    compareWrapper?: InjectableCss;
    priceWrapper?: InjectableCss;
    qtyWrapper?: InjectableCss;
    uomWrapper?: InjectableCss;
    atcWrapper?: InjectableCss;
    chooseOptionsClickableStyles?: InjectableCss;
    cutLengthNotification?: TypographyProps;
    parentPrice?: InjectableCss;
}

export const actionsStyles: ProductListActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
            justify-content: ${(props: any) => (props?.view === "List" ? "space-between" : "flex-start")};
            height: 100%;
        `,
    },
    unitOfMeasureSelect: {
        cssOverrides: {
            formField: css`
                select {
                    padding-right: 13px !important;
                }
                select + span {
                    padding-right: 0 !important;
                    width: 22px !important;
                    right: 0 !important;
                    top: 0 !important;
                }
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: {
            css: css``,
        },
    },
    addToCartWrapper: {
        css: css`
            margin-top: ${(props: any) => (props?.view === "List" ? "36px" : "16px")};
            display: flex;
            flex-wrap: nowrap;
            min-height: 32px;
        `,
    },
    addToCartButton: {
        css: css`
            width: 100%;
        `,
    },
    quantityOrdered: {
        cssOverrides: {
            formField: css`
                input {
                    text-align: center;
                }
            `,
        },
    },
    addToListWrapper: {
        css: css``,
    },
    compareCheckbox: {
        css: css<CheckboxStyleProps>`
            align-items: flex-start;
            span[role="checkbox"] {
                border-radius: 0;
                width: 18px;
                height: 18px;
                border: 2px solid ${getColor("text")};
                &:hover,
                &:focus {
                    cursor: pointer;
                    box-shadow: none;
                }

                svg {
                    width: 18px;
                    height: 18px;
                }
            }
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
                margin-left: 4px !important;
                font-size: 14px;
                letter-spacing: 0.3px;
                line-height: 18px;
            `,
        },
    },
    priceWrapper: {
        css: css`
            min-height: 22px;
            display: flex;
            justify-content: flex-end;
            > section {
                width: auto;
            }
            * {
                line-height: 22px;
                font-size: 20px;
                font-weight: normal;
                color: ${getColor("text.main")};
            }
        `,
    },
    qtyWrapper: {
        css: css`
            width: 65px;
            max-width: 65px;
            flex-basis: 65px;
            padding-right: 15px;

            input {
                height: ${(props: any) => (props?.view === "List" ? "40px" : "32px")} !important;
            }
        `,
    },
    uomWrapper: {
        css: css`
            width: 65px;
            max-width: 65px;
            flex-basis: 65px;
            padding-right: 15px;
            div {
                margin-top: 0 !important;
            }

            select {
                height: ${(props: any) => (props?.view === "List" ? "40px" : "32px")} !important;
            }
            select + span {
                height: ${(props: any) => (props?.view === "List" ? "40px" : "32px")} !important;
            }
        `,
    },
    atcWrapper: {
        css: css`
            width: auto;
            flex: 1;
        `,
    },
    compareAddToListWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            margin-top: 20px;
        `,
    },
    cutLengthNotification: {
        size: "14px",
        lineHeight: "18px",
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
            margin-bottom: 8px;
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: {
                size: 14,
                weight: "normal",
                lineHeight: "16px",
                color: "text.main",
                css: css`
                    letter-spacing: 0.3px;
                    color: ${getColor("text.main")} !important;
                `,
            },
        },
        wrapper: {
            css: css``,
        },
    },
    chooseOptionsClickableStyles: {
        css: css`
            display: block;
            width: 100%;
            font-size: 18px;
            letter-spacing: 0.5px;

            &.list-view-options-button {
                margin-top: 36px;
            }
            &.grid-view-options-button {
                margin-top: 20px;
            }

            &:focus {
                outline-width: 0;
            }
            > button {
                width: 100%;
            }
        `,
    },
    parentPrice: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
};

const styles = actionsStyles;

const StyledWrapperX = getStyledWrapper("div") as any;

const ProductListActions: FC<Props> = ({
    productContext,
    productSettings,
    showPrice,
    showAddToList,
    showCompare,
    view,
    productCompareChecked,
    addProductToCompare,
    removeProductFromCompare,
    isProductCompareFull,
    cutWireSettings,
    customSettings,
}) => {
    const toasterContext = React.useContext(ToasterContext);
    const [styledCount, setStyledCount] = React.useState("");
    const showBadgeAndNotification = (cutWireSettings as any).enableCutWire === "True";
    const displayHighLowPricesForParentProducts =
        (customSettings as any).displayHighLowPricesForParentProducts === "True";

    let showCutLength = false;
    let badges = [] as any;

    if (productContext.product?.properties?.badges) {
        badges = productContext.product?.properties?.badges && JSON.parse(productContext.product.properties["badges"]);

        if (badges?.length > 0) {
            showCutLength = badges.some((badge: any) => badge.DisplayName === "CUTTABLE");
        }
    }

    // work around to prevent undefined for styledChildrenCount when properties are cleared in redux
    React.useEffect(() => {
        if (productContext.product.properties["styledChildrenCount"]) {
            setStyledCount(productContext.product.properties["styledChildrenCount"]);
        }
    }, []);

    const handleChanged = (_: React.SyntheticEvent, value: boolean) => {
        if (value && isProductCompareFull) {
            toasterContext.addToast({
                body: siteMessage("ProductionCompare_MaximumItemsReached"),
                messageType: "warning",
            });
        } else if (value) {
            addProductToCompare({ productId: productContext.product.id });
        } else {
            removeProductFromCompare({ productId: productContext.product.id });
        }
    };

    return (
        <StyledWrapperX {...styles.wrapper} view={view}>
            {view === "List" && (
                <StyledWrapperX {...styles.priceWrapper} view={view}>
                    {showPrice && productContext.product.isVariantParent && displayHighLowPricesForParentProducts && (
                        <StyledWrapper {...styles.parentPrice}>
                            <StyledWrapper>{productContext.product?.properties?.lowPrice}</StyledWrapper>
                            {productContext.product?.properties?.highPrice &&
                                productContext.product?.properties?.lowPrice !==
                                productContext.product?.properties?.highPrice && (
                                    <StyledWrapper>
                                        &nbsp;-&nbsp;
                                        {productContext.product?.properties?.highPrice}
                                    </StyledWrapper>
                                )}
                        </StyledWrapper>
                    )}
                    {showPrice && !productContext.product.isVariantParent && (
                        <ProductPrice
                            product={productContext}
                            showLabel={false}
                            showSavings={true}
                            showSavingsAmount={productSettings.showSavingsAmount}
                            showSavingsPercent={productSettings.showSavingsPercent}
                            extendedStyles={styles.price}
                        />
                    )}
                </StyledWrapperX>
            )}

            {!productContext.product.isVariantParent ? (
                <StyledWrapperX {...styles.addToCartWrapper} view={view}>
                    {(productContext.product.unitOfMeasures as any).length > 1 && (
                        <StyledWrapperX {...styles.uomWrapper} view={view}>
                            <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} labelOverride="" />
                        </StyledWrapperX>
                    )}
                    <StyledWrapperX {...styles.qtyWrapper} view={view}>
                        <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} labelOverride="" />
                    </StyledWrapperX>
                    <StyledWrapperX {...styles.atcWrapper} view={view}>
                        <ProductAddToCartButton
                            data-test-selector={`actionsAddToCart${productContext.product.id}`}
                            extendedStyles={styles.addToCartButton}
                            sizeVariant={view === "List" ? "large" : "small"}
                        />{" "}
                    </StyledWrapperX>
                </StyledWrapperX>
            ) : (
                <Clickable
                    href={productContext.productInfo.productDetailPath}
                    {...styles.chooseOptionsClickableStyles}
                    className={view === "List" ? "list-view-options-button" : "grid-view-options-button"}
                >
                    <Button variant="primary">
                        {translate("Choose from {0} Options").replace("{0}", styledCount)}
                    </Button>
                </Clickable>
            )}

            <StyledWrapperX {...styles.compareAddToListWrapper}>
                {showCutLength && showBadgeAndNotification && (
                    <Typography {...styles.cutLengthNotification}>
                        <Icon src={Measure} size={16} color="text" />
                        &nbsp;&nbsp;{translate("Add Cut Lengths in Cart")}&nbsp;&nbsp;
                        <Tooltip
                            text={siteMessage("Add_Cut_Lengths_in_Cart") as string}
                        />
                    </Typography>
                )}
                {showAddToList && (
                    <StyledWrapper {...styles.addToListWrapper}>
                        <ProductAddToListLink
                            data-test-selector="addToListLink"
                            extendedStyles={styles.productAddToListLinkStyles}
                            labelOverride={translate("Shopping List")}
                            showIcon={true}
                        />
                    </StyledWrapper>
                )}
                {showCompare && productSettings.enableProductComparisons && (
                    <StyledWrapper {...styles.compareWrapper}>
                        <Checkbox {...styles.compareCheckbox} checked={productCompareChecked} onChange={handleChanged}>
                            {translate("Compare")}
                        </Checkbox>
                    </StyledWrapper>
                )}
            </StyledWrapperX>
        </StyledWrapperX>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductListActions));
